import {
  Link,
  useNavigate
} from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Breadcrumb,
  Col
} from "react-bootstrap";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page as Paging,
  PageSettingsModel,
  Filter,
  Resize,
  FilterSettingsModel
} from "@syncfusion/ej2-react-grids";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider
} from "@whitespace/whitespace-ui";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { useEffect, useState, useCallback } from "react";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import { parseUsingParseInt } from "helpers/stringUtils";
import INonWorkingPeriod from "interfaces/INonWorkingPeriod";
import EditTemplate from "./Templates/EditTemplate";

import "styles/sf-datagrid.scss";

const pageSize: number = parseUsingParseInt(process.env.REACT_APP_PAGE_SIZE);
const pageSettings: PageSettingsModel = { pageSize, pageSizes: [5, 10, 20, 50, "All"] };
const filterSettings: FilterSettingsModel = { type: "Excel" };
let grid: GridComponent | undefined;

const activeTemplate = (props: any): any => (props.active ? "Active" : "Inactive");
const activeTemplateInstance = activeTemplate;

const ManageNonWorkingPeriods = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [renderingComplete, setRenderingComplete] = useState(false);
  const {
    isSending,
    errors,
    data,
    statusCode,
    sendRequest
  } = useAmplifyREST();
  const navigate = useNavigate();

  const [
    nonWorkingPeriods,
    setNonWorkingPeriods
  ] = useState<INonWorkingPeriod[] | undefined>(undefined);

  useEffect(() => {
    sendRequest("settings/nonworkingperiods", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data && data.nonWorkingPeriods) setNonWorkingPeriods(data.nonWorkingPeriods);
  }, [data, setNonWorkingPeriods]);

  useEffect(() => {
    if (errors) setShowExceptions(true);
  }, [errors]);

  useEffect(() => {
    if (![0, 200].includes(statusCode)) {
      setRenderingComplete(true);
    }
  }, [statusCode]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const isProcessing = isSending || !renderingComplete;

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="nonworkingperiods-heading">
              Non-Working Period (NWP) Management
              <Spinner
                animation="grow"
                visible={isProcessing}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
          <div className="float-end">
            <IconButton
              data-testid="add-nonworkingperiod"
              iconType={IconButtonType.Add}
              variant="outline-primary btn-create"
              onClick={() => navigate(RoutePathsEnum.SettingsNonWorkingPeriodsAdd)}
            >
              Add New NWP
            </IconButton>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={8} className="d-flex">
              <Breadcrumb className="left-margin-large no-wrap" data-testid="breadcrumb">
                <Breadcrumb.Item
                  data-testid="breadcrumb-settings"
                  linkAs={Link}
                  linkProps={{ to: RoutePathsEnum.Settings }}
                  onClick={() => navigate(RoutePathsEnum.Settings)}
                >
                  Settings
                </Breadcrumb.Item>
                <Breadcrumb.Item active data-testid="breadcrumb-nonworkingperiods">Non-Working Periods</Breadcrumb.Item>
              </Breadcrumb>
            </Col>
          </Row>
          <Divider />
          <section className="pt-2">
            <Container fluid>
              <Row>
                <div className="control-pane">
                  <div className="control-section" data-testid="grid">

                    <GridComponent
                      ref={(g:GridComponent) => { grid = g; }}
                      id="nonWorkingPeriodsGrid"
                      data-testid="nonWorkingPeriodsGrid"
                      allowPaging
                      allowResizing
                      pageSettings={pageSettings}
                      allowSorting
                      allowFiltering
                      filterSettings={filterSettings}
                      allowSelection={false}
                      dataSource={nonWorkingPeriods}
                      actionComplete={() => { makeGridAACompliant(grid!, () => { setRenderingComplete(true); }); }}
                      loadingIndicator={{ indicatorType: "Shimmer" }}
                      type=""
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="id"
                          headerText="id"
                          visible={false}
                          isPrimaryKey
                        />
                        <ColumnDirective
                          field="name"
                          headerText="Name"
                        />
                        <ColumnDirective
                          field="dateFrom"
                          type="date"
                          headerText="Date From"
                          format="dd/MM/yyyy"
                          width={80}
                        />
                        <ColumnDirective
                          field="dateTo"
                          type="date"
                          headerText="Date To"
                          format="dd/MM/yyyy"
                          width={80}
                        />
                        <ColumnDirective
                          field="description"
                          headerText="Description"
                          hideAtMedia="(min-width:1200px)"
                        />
                        <ColumnDirective
                          field="severity"
                          headerText="Severity"
                          hideAtMedia="(min-width:800px)"
                          width={80}
                        />
                        <ColumnDirective
                          field="active"
                          headerText="Status"
                          template={activeTemplateInstance}
                          width={80}

                        />
                        <ColumnDirective
                          headerText=""
                          width={50}
                          allowSorting={false}
                          allowResizing={false}
                          template={EditTemplate}
                          allowFiltering={false}
                        />
                      </ColumnsDirective>
                      <Inject services={[Filter, Paging, Sort, Resize]} />
                    </GridComponent>

                  </div>

                </div>
              </Row>
            </Container>
          </section>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default ManageNonWorkingPeriods;
